import * as yup from 'yup';
import moment from 'moment';
import type { MessageDescriptor } from 'react-intl';

interface QuestionMetadata {
  position: number;
  tour_question_id: number;
  required?: boolean;
  input_type?: string;
  date_format?: string;
  minimum_value?: number;
}

const isValidDate = (answer: any, dateFormat?: string): boolean => {
  if (answer && dateFormat) {
    return moment(answer, dateFormat, true).isValid();
  }
  return false;
};

const meetsAgeRequirement = (
  answer: string,
  dateFormat: string,
  minimumValue: number
): boolean => {
  const birthdateMomentObject = moment(answer, dateFormat);
  const birthdate = birthdateMomentObject.toDate();
  const allowedDate = new Date();
  const allowedYear = allowedDate.getFullYear() - minimumValue;
  allowedDate.setFullYear(allowedYear);
  return birthdate <= allowedDate;
};

export const createPageOneValidationSchema = (
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, string | number>
  ) => string,
  options: {
    phoneMandatory: boolean;
    showZipCodeOnLive: boolean;
    isLiveBooking: boolean;
    isGuestInfoCollectionEnabled: boolean;
    shouldCollectGuestInfo: boolean;
    isAffiliate: boolean;
    fieldsToHide: string[];
    shouldShowWaiver: boolean;
    shouldShowQuestions: boolean;
    questions?: QuestionMetadata[];
    shouldValidatePrivacyPolicy?: boolean;
  }
) => {
  const {
    phoneMandatory,
    showZipCodeOnLive,
    isLiveBooking,
    isGuestInfoCollectionEnabled,
    shouldCollectGuestInfo,
    isAffiliate,
    fieldsToHide,
    shouldShowWaiver,
    shouldShowQuestions,
    questions = [],
    shouldValidatePrivacyPolicy = false,
  } = options;

  const questionsByTourQuestionId = new Map(
    questions.map((q) => [q.tour_question_id, q])
  );

  const shouldShowNameFields = !fieldsToHide.includes('name');
  const shouldShowEmailField = !fieldsToHide.includes('email');

  const schema: Record<string, any> = {};

  if (isGuestInfoCollectionEnabled && shouldShowNameFields) {
    schema.first_name = yup
      .string()
      .required(formatMessage({ defaultMessage: 'First name is required' }))
      .trim();

    schema.last_name = yup
      .string()
      .required(formatMessage({ defaultMessage: 'Last name is required' }))
      .trim();
  }

  if (isGuestInfoCollectionEnabled && shouldShowEmailField) {
    if (isLiveBooking) {
      schema.email = yup
        .string()
        .email(formatMessage({ defaultMessage: 'Invalid email address' }));
    } else if (isAffiliate) {
      schema.email = yup
        .string()
        .email(formatMessage({ defaultMessage: 'Invalid email address' }));
    } else {
      schema.email = yup
        .string()
        .email(formatMessage({ defaultMessage: 'Invalid email address' }))
        .required(formatMessage({ defaultMessage: 'Email is required' }))
        .trim();
    }
  }

  if (isGuestInfoCollectionEnabled) {
    if (phoneMandatory || (isLiveBooking && phoneMandatory)) {
      schema.phone = yup
        .string()
        .required(formatMessage({ defaultMessage: 'Phone is required' }));
    } else {
      schema.phone = yup.string();
    }
  }

  if (isLiveBooking && showZipCodeOnLive) {
    schema.zip_code = yup
      .string()
      .required(formatMessage({ defaultMessage: 'ZIP code is required' }));
  }

  if (shouldCollectGuestInfo) {
    schema.booker_unit_id = yup.number().required();
    schema.additional_guests = yup.array().of(
      yup.object().shape({
        first_name: yup.string(),
        last_name: yup.string(),
        position: yup.number(),
        unit_type_id: yup.number(),
      })
    );
  }

  if (shouldShowWaiver) {
    const waiverMessage = formatMessage({
      defaultMessage: 'Terms and conditions should be accepted',
    });
    schema.waiver_accepted = yup
      .boolean()
      .oneOf([true], waiverMessage)
      .required(waiverMessage);
  }

  if (shouldShowQuestions && !isAffiliate) {
    schema.checkout_questions = yup.array().of(
      yup.object().shape({
        tour_question_id: yup.number().required(),
        answer: yup.mixed().test('validate-answer', function (value) {
          const { tour_question_id } = this.parent;
          const question = questionsByTourQuestionId.get(tour_question_id);

          if (!question) {
            return true;
          }

          const { required, input_type, date_format, minimum_value } = question;

          if (input_type === 'waiver') {
            if (
              required &&
              (value === undefined || value === 'no' || value === false)
            ) {
              return this.createError({
                message: formatMessage({
                  id: 'RESPONSE_IS_REQUIRED',
                  defaultMessage: 'Response is required',
                }),
              });
            }
            return true;
          }

          if (!required) {
            return true;
          }

          if (input_type === 'text') {
            if (
              !value ||
              (typeof value === 'string' && value.trim().length === 0)
            ) {
              return this.createError({
                message: formatMessage({
                  id: 'ANSWER_CANNOT_BE_BLANK',
                  defaultMessage: 'Answer cannot be blank',
                }),
              });
            }
            return true;
          }

          if (input_type === 'date') {
            if (
              !value ||
              (typeof value === 'string' && value.trim().length === 0)
            ) {
              if (required) {
                return this.createError({
                  message: formatMessage({
                    id: 'DATE_IS_INCOMPLETE',
                    defaultMessage: 'Date is incomplete',
                  }),
                });
              }
              return true;
            }

            if (!isValidDate(value, date_format)) {
              return this.createError({
                message: formatMessage({
                  id: 'DATE_IS_INCOMPLETE',
                  defaultMessage: 'Date is incomplete',
                }),
              });
            }

            if (minimum_value && date_format && typeof value === 'string') {
              if (!meetsAgeRequirement(value, date_format, minimum_value)) {
                const ageMessage = formatMessage(
                  {
                    defaultMessage:
                      'Sorry, you need to be at least {age} years old to proceed',
                  },
                  { age: minimum_value }
                );
                return this.createError({
                  message: ageMessage,
                });
              }
            }
            return true;
          }

          // for multi-select questions
          // User may select and unselect options, leaving an empty array
          if (Array.isArray(value)) {
            const filtered = value.filter(
              (v) => v !== null && v !== undefined && v !== ''
            );
            if (filtered.length === 0) {
              return this.createError({
                message: formatMessage({
                  defaultMessage: 'Please select an option',
                }),
              });
            }
            return true;
          }

          // for single-select and multi-select questions
          // if no option is selected
          if (value === null || value === undefined || value === '') {
            return this.createError({
              message: formatMessage({
                defaultMessage: 'Please select an option',
              }),
            });
          }

          return true;
        }),
      })
    );
  }

  if (shouldValidatePrivacyPolicy) {
    const privacyMessage = formatMessage({
      defaultMessage: 'Privacy policy should be accepted',
    });
    schema.privacy_policy_accepted = yup
      .boolean()
      .oneOf([true], privacyMessage)
      .required(privacyMessage);
  }

  return yup.object().shape(schema);
};
